import {useMutation, UseMutationResult} from '@tanstack/react-query';

import {Locale} from '../../provider/locale/locale';
import {useSnackbar} from '../../provider/snackbar/snackbar-hook';
import {patchAndDeserialize, postAndDeserialize} from '../../util/api-adapter';

import {
    bulkReviewVisibilityResultSchema,
    BulkReviewVisibilityResultType,
    ReviewDataType,
    reviewVisibilityResponseSchema,
    ReviewVisibilityResponseType,
} from './reviews-type';

async function fetchToggleReviewVisibility(reviewId: number, isHidden: boolean): Promise<ReviewVisibilityResponseType> {
    return patchAndDeserialize<ReviewVisibilityResponseType>(
        `/v4/reviews/${reviewId}/visibility/`,
        reviewVisibilityResponseSchema,
        {isHidden}
    );
}

function fetchBulkToggleReviewVisibility(
    reviewIds: Array<number>,
    isHidden: boolean
): Promise<BulkReviewVisibilityResultType> {
    return postAndDeserialize<BulkReviewVisibilityResultType>(
        '/v4/reviews/visibility/bulk/',
        bulkReviewVisibilityResultSchema,
        {
            reviewIds,
            isHidden,
        }
    );
}

type ToggleReviewVisibilityVariablesType = {
    reviewId: number;
    isHidden: boolean;
};

type UseToggleReviewVisibilityMutationOptionsType = {
    updateReview: (reviewId: number, updateData: Partial<ReviewDataType>) => void;
    refreshReviews?: () => Promise<unknown>;
};

export function useToggleReviewVisibilityMutation(
    options: UseToggleReviewVisibilityMutationOptionsType
): UseMutationResult<ReviewVisibilityResponseType, unknown, ToggleReviewVisibilityVariablesType> {
    const {updateReview, refreshReviews} = options;
    const {snackbar} = useSnackbar();

    return useMutation({
        mutationFn: ({reviewId, isHidden}) => fetchToggleReviewVisibility(reviewId, isHidden),
        onSuccess: async (data, variables) => {
            updateReview(variables.reviewId, {isHidden: data.isHidden});
            await refreshReviews?.();

            if (data.isHidden) {
                snackbar.success({
                    message: <Locale stringKey="REVIEWS__REVIEW_HIDE__SUCCESS__TITLE" />,
                    description: <Locale stringKey="REVIEWS__REVIEW_HIDE__SUCCESS__DESCRIPTION" />,
                });
            } else {
                snackbar.success({
                    message: <Locale stringKey="REVIEWS__REVIEW_UNHIDE__SUCCESS__TITLE" />,
                    description: <Locale stringKey="REVIEWS__REVIEW_UNHIDE__SUCCESS__DESCRIPTION" />,
                });
            }
        },
        onError: (_error, variables) => {
            if (variables.isHidden) {
                snackbar.error({
                    message: <Locale stringKey="REVIEWS__REVIEW_HIDE__ERROR__TITLE" />,
                    description: <Locale stringKey="REVIEWS__REVIEW_HIDE__ERROR__DESCRIPTION" />,
                });
            } else {
                snackbar.error({
                    message: <Locale stringKey="REVIEWS__REVIEW_UNHIDE__ERROR__TITLE" />,
                    description: <Locale stringKey="REVIEWS__REVIEW_UNHIDE__ERROR__DESCRIPTION" />,
                });
            }
        },
    });
}

type BulkToggleReviewVisibilityVariablesType = {
    reviewIds: Array<number>;
    isHidden: boolean;
};

type UseBulkToggleReviewVisibilityMutationOptionsType = {
    updateReview: (reviewId: number, updateData: Partial<ReviewDataType>) => void;
    onFlowComplete?: () => void;
    refreshReviews?: () => Promise<unknown>;
};

export function useBulkToggleReviewVisibilityMutation(
    options: UseBulkToggleReviewVisibilityMutationOptionsType
): UseMutationResult<BulkReviewVisibilityResultType, unknown, BulkToggleReviewVisibilityVariablesType> {
    const {updateReview, onFlowComplete, refreshReviews} = options;
    const {snackbar} = useSnackbar();

    return useMutation({
        mutationFn: ({reviewIds, isHidden}) => fetchBulkToggleReviewVisibility(reviewIds, isHidden),
        onError: (_error, variables) => {
            if (variables.isHidden) {
                snackbar.error({
                    message: <Locale stringKey="REVIEWS__BULK_HIDE__ERROR__TITLE" />,
                    description: <Locale stringKey="REVIEWS__BULK_HIDE__ERROR__DESCRIPTION" />,
                });
            } else {
                snackbar.error({
                    message: <Locale stringKey="REVIEWS__BULK_UNHIDE__ERROR__TITLE" />,
                    description: <Locale stringKey="REVIEWS__BULK_UNHIDE__ERROR__DESCRIPTION" />,
                });
            }
        },
        onSuccess: async (result, variables) => {
            const {isHidden} = variables;
            const processedCount = result.processedReviewIds.length;
            const skippedCount = result.skippedReviewIds.length;

            result.processedReviewIds.forEach((reviewId) => {
                updateReview(reviewId, {isHidden});
            });

            await refreshReviews?.();

            if (skippedCount > 0) {
                snackbar.success({
                    message: (
                        <Locale
                            stringKey={
                                isHidden ? 'REVIEWS__BULK_HIDE__SUCCESS__TITLE' : 'REVIEWS__BULK_UNHIDE__SUCCESS__TITLE'
                            }
                        />
                    ),
                    description: (
                        <Locale
                            stringKey={
                                isHidden
                                    ? 'REVIEWS__BULK_HIDE__PARTIAL_DESCRIPTION'
                                    : 'REVIEWS__BULK_UNHIDE__PARTIAL_DESCRIPTION'
                            }
                            valueMap={{
                                processedCount: String(processedCount),
                                skippedCount: String(skippedCount),
                            }}
                        />
                    ),
                });
            } else {
                snackbar.success({
                    message: (
                        <Locale
                            stringKey={
                                isHidden ? 'REVIEWS__BULK_HIDE__SUCCESS__TITLE' : 'REVIEWS__BULK_UNHIDE__SUCCESS__TITLE'
                            }
                        />
                    ),
                    description: (
                        <Locale
                            stringKey={
                                isHidden
                                    ? 'REVIEWS__BULK_HIDE__SUCCESS__DESCRIPTION'
                                    : 'REVIEWS__BULK_UNHIDE__SUCCESS__DESCRIPTION'
                            }
                            valueMap={{processedCount: String(processedCount)}}
                        />
                    ),
                });
            }

            onFlowComplete?.();
        },
    });
}
